// O cliente da IA de curso — Contabiliza.AI (Escola e Aprovação) e, depois, Peric.IA.
//
// ---------------------------------------------------------------------------
// Por que `fetch` cru e não `supabase.functions.invoke`
// ---------------------------------------------------------------------------
// O `invoke` do supabase-js só devolve a resposta INTEIRA, e a resposta aqui é um texto que
// leva segundos para sair. Texto que aparece aos poucos é a diferença entre "está pensando"
// e "travou" — e o `perguntar-ia` responde em `text/plain` justamente por isso, com os
// metadados em cabeçalho, porque o corpo é a resposta.
//
// ---------------------------------------------------------------------------
// O que este arquivo NÃO decide
// ---------------------------------------------------------------------------
// Nada de acesso. Quem decide se o aluno pode perguntar, em qual conversa, com que limite e
// sobre qual corpus é o banco (`perguntar_ia_comecar`, `buscar_ia` — migrations 000068 e
// 000072), e a Edge Function chama COMO O ALUNO para que valha a sessão dele. Aqui só se
// monta o pedido e se lê o fluxo.
import { supabase } from "./supabase";

const URL_BASE = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/** Um trecho citado, para o rodapé da resposta. Nunca vem o texto do trecho: o corpus não
 *  desce ao navegador pedaço por pedaço. */
export type Fonte = {
  titulo: string;
  curso_slug: string | null;
  aula_titulo: string | null;
  pagina_inicio: number | null;
  escopo: "curso" | "plataforma";
  distancia: number;
};

export type MetaResposta = {
  modo?: string;
  recusa?: string;
  n_trechos?: number;
  distancia_min?: number;
  trancado?: boolean;
};

export type Mensagem = {
  id: string;
  papel: "aluno" | "ia";
  conteudo: string;
  fontes: Fonte[] | null;
  recusa: string | null;
  criado_em: string;
};

export type Conversa = {
  id: string;
  titulo: string;
  curso_slug: string;
  atualizado_em: string;
  fixada: boolean;
};

/** O motivo pelo qual a IA se recusou, em português de gente. As chaves são as que o banco
 *  e a function devolvem — trocar uma delas aqui sem trocar lá deixa a tela muda. */
export function mensagemDaRecusa(motivo: string | null | undefined): string | null {
  switch (motivo) {
    case "sem-perfil":
      return "Não encontrei o seu cadastro. Saia e entre de novo.";
    case "sem-acesso":
      return "Este assistente não faz parte dos cursos que você tem hoje.";
    case "pergunta-vazia":
      return "Escreva a sua pergunta.";
    case "pergunta-longa":
      return "A pergunta ficou longa demais. Tente resumir em uma ou duas frases.";
    case "limite":
      return "Você fez muitas perguntas em pouco tempo. Espere alguns segundos e tente de novo.";
    case "fora-do-escopo":
      return null; // a própria resposta já explica, e ela vem no corpo
    case "falha-embedding":
    case "falha-busca":
    case "falha-geracao":
    case "erro-interno":
      return "Não consegui responder agora. Tente de novo em alguns segundos.";
    case "saida-cortada":
      return "Interrompi a resposta por segurança. Tente reformular a pergunta.";
    case "sessao_invalida":
      return "A sua sessão expirou. Saia e entre de novo.";
    default:
      return motivo ? "Não consegui responder agora. Tente de novo." : null;
  }
}

export type Pedido = {
  card: string;
  curso: string;
  pergunta: string;
  conversaId?: string | null;
  /** chamado a cada pedaço de texto que chega */
  aoReceber: (textoAcumulado: string) => void;
  sinal?: AbortSignal;
};

export type Resultado =
  | { ok: true; conversaId: string; texto: string; fontes: Fonte[]; meta: MetaResposta }
  | { ok: false; motivo: string };

/**
 * Pergunta, e vai entregando o texto conforme ele chega.
 *
 * O `aoReceber` é chamado com o texto ACUMULADO, não com o delta: a tela quer renderizar o
 * estado, não costurar pedaços — e costurar em dois lugares é a receita de perder um
 * caractere numa borda de chunk.
 */
export async function perguntar(p: Pedido): Promise<Resultado> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return { ok: false, motivo: "sessao_invalida" };

  let r: Response;
  try {
    r = await fetch(`${URL_BASE}/functions/v1/perguntar-ia`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: ANON,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        card: p.card,
        curso: p.curso,
        pergunta: p.pergunta,
        conversa_id: p.conversaId ?? null,
      }),
      signal: p.sinal,
    });
  } catch (e) {
    // abortar é escolha do usuário, não falha
    if ((e as Error)?.name === "AbortError") return { ok: false, motivo: "cancelado" };
    return { ok: false, motivo: "erro-interno" };
  }

  // Erro vem em JSON, antes de o fluxo começar. É o único caminho em que o corpo não é
  // texto, e por isso o tipo de conteúdo decide como ler.
  if (!r.ok) {
    const tipo = r.headers.get("content-type") ?? "";
    if (tipo.includes("application/json")) {
      const corpo = await r.json().catch(() => null);
      return { ok: false, motivo: String(corpo?.motivo ?? corpo?.erro ?? "erro-interno") };
    }
    return { ok: false, motivo: r.status === 401 ? "sessao_invalida" : "erro-interno" };
  }

  const conversaId = r.headers.get("x-ia-conversa") ?? (p.conversaId ?? "");
  const leCabecalho = <T,>(nome: string, cai: T): T => {
    const cru = r.headers.get(nome);
    if (!cru) return cai;
    try { return JSON.parse(decodeURIComponent(cru)) as T; } catch { return cai; }
  };
  const meta = leCabecalho<MetaResposta>("x-ia-meta", {});
  const fontes = leCabecalho<Fonte[]>("x-ia-fontes", []);

  let texto = "";
  const leitor = r.body?.getReader();
  if (leitor) {
    const dec = new TextDecoder();
    try {
      for (;;) {
        const { done, value } = await leitor.read();
        if (done) break;
        texto += dec.decode(value, { stream: true });
        p.aoReceber(texto);
      }
    } catch (e) {
      // fluxo cortado no meio: o que chegou vale, e é melhor mostrar do que descartar
      if ((e as Error)?.name !== "AbortError") {
        return texto
          ? { ok: true, conversaId, texto, fontes, meta: { ...meta, recusa: "fluxo-cortado" } }
          : { ok: false, motivo: "erro-interno" };
      }
    }
  }

  return { ok: true, conversaId, texto, fontes, meta };
}

// ---------------------------------------------------------------------------
// Histórico — LEITURA direta, escrita nunca
// ---------------------------------------------------------------------------
// `authenticated` tem SELECT em `ia_conversa` e `ia_mensagem` e mais nada (000072): quem
// grava são as duas funções chamadas pela Edge Function. Se o front pudesse gravar, ele
// decidiria o que fica guardado — e o que fica guardado volta para o modelo na volta
// seguinte.

export async function listarConversas(card: string, curso: string): Promise<Conversa[]> {
  // Fixadas primeiro — é para isso que o aluno fixa. Dentro de cada grupo, a mais
  // recente em cima.
  const { data, error } = await supabase
    .from("ia_conversa")
    .select("id, titulo, curso_slug, atualizado_em, fixada")
    .eq("card_slug", card)
    .eq("curso_slug", curso)
    .order("fixada", { ascending: false })
    .order("atualizado_em", { ascending: false })
    .limit(40);
  if (error) return [];
  return (data ?? []) as Conversa[];
}

/**
 * As sugestões de primeira pergunta, do BANCO (`ia_sugestao`, 000105) e por curso.
 *
 * Estavam cravadas em `IaDoCurso.tsx`, e como a tela é uma para todas as IAs, a Peric.IA
 * sugeria "Qual o prazo de entrega da ECD?" numa pós de perícia. Texto que o aluno lê é dado
 * — a mesma razão do `irp_perguntas` (000065).
 *
 * Devolve as do CURSO primeiro e completa com as da plataforma, que valem para todos. Se o
 * banco não responder, devolve lista vazia: a tela sem sugestão é melhor que sugestão errada.
 */
export async function lerSugestoes(curso: string, quantas = 3): Promise<string[]> {
  const { data, error } = await supabase
    .from("ia_sugestao")
    .select("texto, escopo, curso_slug, ordem")
    .or(`curso_slug.eq.${curso},escopo.eq.plataforma`)
    .order("escopo", { ascending: true })   // 'curso' vem antes de 'plataforma'
    .order("ordem", { ascending: true });
  if (error || !data) return [];
  const doCurso = data.filter((s) => s.curso_slug === curso).map((s) => s.texto as string);
  const daPlataforma = data.filter((s) => s.escopo === "plataforma").map((s) => s.texto as string);
  // duas do curso e uma da plataforma: a terceira ensina que a IA também resolve conta e
  // acesso, que é metade do que o aluno pergunta na prática
  return [...doCurso.slice(0, quantas - 1), ...daPlataforma.slice(0, 1)].slice(0, quantas);
}

/** Fixa/desafixa uma conversa do próprio aluno (RPC da 000089 — `authenticated` não tem
 *  UPDATE na tabela; a função decide pelo dono). Devolve se achou. */
export async function fixarConversa(id: string, fixada: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("ia_conversa_fixar", {
    p_conversa: id,
    p_fixada: fixada,
  });
  return !error && data === true;
}

/** Exclui uma conversa do próprio aluno — as mensagens vão junto (FK em cascata); o custo
 *  em `ia_uso` fica, porque é contabilidade. Devolve se achou. */
export async function excluirConversa(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("ia_conversa_excluir", { p_conversa: id });
  return !error && data === true;
}

export async function lerMensagens(conversaId: string): Promise<Mensagem[]> {
  const { data, error } = await supabase
    .from("ia_mensagem")
    .select("id, papel, conteudo, fontes, recusa, criado_em")
    .eq("conversa_id", conversaId)
    .order("criado_em", { ascending: true });
  if (error) return [];
  return (data ?? []) as Mensagem[];
}
